import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import {
  type AccountOthersData,
  GET_ACCOUNT_OTHERS
} from '../queries/accountOthersQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountOthersData;
  isDate?: boolean;
}

const ACTIVITY_COUNTS_FIELDS: FieldMapping[] = [
  { apiField: 'investorCount', formField: 'investorCount' },
  { apiField: 'totalActivities', formField: 'totalActivities' },
  { apiField: 'totalContacts', formField: 'totalContacts' },
  {
    apiField: 'totalContactsLastUpdatedOn',
    formField: 'totalContactsLastUpdatedOn',
    isDate: true
  },
  {
    apiField: 'totalInvestorOpportunities',
    formField: 'totalInvestorOpportunities'
  },
  {
    apiField: 'totalInvestorOpportunitiesLastUpdatedOn',
    formField: 'totalInvestorOpportunitiesLastUpdatedOn',
    isDate: true
  },
  { apiField: 'totalOpenIos', formField: 'totalOpenIos' },
  { apiField: 'totalNotes', formField: 'totalNotes' },
  { apiField: 'openDeals', formField: 'openDeals' },
  {
    apiField: 'openDealsLastUpdatedTime',
    formField: 'openDealsLastUpdatedTime',
    isDate: true
  },
  { apiField: 'numberOfEmployees', formField: 'numberOfEmployees' },
  {
    apiField: 'warmIntroProcessCount',
    formField: 'warmIntroProcessCount'
  },
  {
    apiField: 'warmIntroProcessDate',
    formField: 'warmIntroProcessDate',
    isDate: true
  },
  {
    apiField: 'lastActivityAddedOn',
    formField: 'lastActivityAddedOn',
    isDate: true
  },
  {
    apiField: 'lastNoteAddedOn',
    formField: 'lastNoteAddedOn',
    isDate: true
  },
  {
    apiField: 'lastSynchedToNotion',
    formField: 'lastSynchedToNotion',
    isDate: true
  }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountOthersData,
  initial: AccountOthersData
): Record<string, null | number | string> {
  const changes: Record<string, null | number | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else {
        // Handle number fields
        const num =
          currentVal === null || currentVal === ''
            ? null
            : typeof currentVal === 'number'
              ? currentVal
              : Number(currentVal);
        changes[apiField] = num === null || Number.isNaN(num) ? null : num;
      }
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

// Validation schema for integer fields
const integerSchema = z.string().refine(
  val => {
    if (val === '') return true;
    const num = Number(val);
    return !isNaN(num) && Number.isInteger(num);
  },
  {
    message: 'Please enter a valid whole number'
  }
);

const NUMBER_FIELDS = [
  'investorCount',
  'totalActivities',
  'totalContacts',
  'totalInvestorOpportunities',
  'totalOpenIos',
  'totalNotes',
  'openDeals',
  'numberOfEmployees',
  'warmIntroProcessCount'
] as const;

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        investorCount
        totalActivities
        totalContacts
        totalContactsLastUpdatedOn
        totalInvestorOpportunities
        totalInvestorOpportunitiesLastUpdatedOn
        totalOpenIos
        totalNotes
        openDeals
        openDealsLastUpdatedTime
        numberOfEmployees
        warmIntroProcessCount
        warmIntroProcessDate
        lastActivityAddedOn
        lastNoteAddedOn
        lastSynchedToNotion
      }
    }
  }
`;

export interface EditActivityCountsDialogProps {
  accountId: string;
  initialValue: AccountOthersData;
  isOpen: boolean;
  onChange: (data: AccountOthersData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountOthersData;
}

export function EditActivityCountsDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditActivityCountsDialogProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_OTHERS, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(ACTIVITY_COUNTS_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const validateField = (field: string, fieldValue: string) => {
    if (NUMBER_FIELDS.includes(field as (typeof NUMBER_FIELDS)[number])) {
      const result = integerSchema.safeParse(fieldValue);
      if (!result.success && result.error.issues.length > 0) {
        setErrors(prev => ({
          ...prev,
          [field]: result.error.issues[0]?.message ?? 'Invalid number'
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const updateField = <K extends keyof AccountOthersData>(
    field: K,
    fieldValue: AccountOthersData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleNumberChange = (field: keyof AccountOthersData, raw: string) => {
    validateField(field as string, raw);
    const num = raw.trim() === '' ? null : Number(raw);
    updateField(
      field,
      (num === null || Number.isNaN(num)
        ? null
        : num) as AccountOthersData[keyof AccountOthersData]
    );
  };

  const formatNumber = (val: null | number) =>
    val === null ? '' : String(val);

  const handleSave = async () => {
    // Validate all number fields before saving
    const newErrors: Record<string, string> = {};
    for (const field of NUMBER_FIELDS) {
      const fieldValue = value[field];
      const strValue = fieldValue === null ? '' : String(fieldValue);
      const result = integerSchema.safeParse(strValue);
      if (!result.success && result.error.issues.length > 0) {
        newErrors[field] = result.error.issues[0]?.message ?? 'Invalid number';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save activity counts');
      } else {
        setErrors({});
        toast.success('Activity counts saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving activity counts:', error);
      toast.error('Failed to save activity counts');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setErrors({});
    }
    onOpenChange(open);
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={handleOpenChange}
      onSave={handleSave}
      title="Edit Activity Counts"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          error={errors.investorCount}
          label="Investor Count"
          onChange={raw => {
            handleNumberChange('investorCount', raw);
          }}
          type="number"
          value={formatNumber(value.investorCount)}
        />
        <EditFormField
          error={errors.totalActivities}
          label="Total Activities"
          onChange={raw => {
            handleNumberChange('totalActivities', raw);
          }}
          type="number"
          value={formatNumber(value.totalActivities)}
        />
        <EditFormField
          error={errors.totalContacts}
          label="Total Contacts"
          onChange={raw => {
            handleNumberChange('totalContacts', raw);
          }}
          type="number"
          value={formatNumber(value.totalContacts)}
        />
        <EditFormField
          label="Total Contacts (Last Updated On)"
          onChange={totalContactsLastUpdatedOn => {
            updateField(
              'totalContactsLastUpdatedOn',
              totalContactsLastUpdatedOn
            );
          }}
          type="date"
          value={toDateInputValue(value.totalContactsLastUpdatedOn)}
        />
        <EditFormField
          error={errors.totalInvestorOpportunities}
          label="Total Investor Opportunities"
          onChange={raw => {
            handleNumberChange('totalInvestorOpportunities', raw);
          }}
          type="number"
          value={formatNumber(value.totalInvestorOpportunities)}
        />
        <EditFormField
          label="Total Investor Opportunities (Last Updated On)"
          onChange={totalInvestorOpportunitiesLastUpdatedOn => {
            updateField(
              'totalInvestorOpportunitiesLastUpdatedOn',
              totalInvestorOpportunitiesLastUpdatedOn
            );
          }}
          type="date"
          value={toDateInputValue(
            value.totalInvestorOpportunitiesLastUpdatedOn
          )}
        />
        <EditFormField
          error={errors.totalOpenIos}
          label="Total Open IOs"
          onChange={raw => {
            handleNumberChange('totalOpenIos', raw);
          }}
          type="number"
          value={formatNumber(value.totalOpenIos)}
        />
        <EditFormField
          error={errors.totalNotes}
          label="Total Notes"
          onChange={raw => {
            handleNumberChange('totalNotes', raw);
          }}
          type="number"
          value={formatNumber(value.totalNotes)}
        />
        <EditFormField
          error={errors.openDeals}
          label="Open Deals"
          onChange={raw => {
            handleNumberChange('openDeals', raw);
          }}
          type="number"
          value={formatNumber(value.openDeals)}
        />
        <EditFormField
          label="Open Deals (Last Updated Time)"
          onChange={openDealsLastUpdatedTime => {
            updateField('openDealsLastUpdatedTime', openDealsLastUpdatedTime);
          }}
          type="date"
          value={toDateInputValue(value.openDealsLastUpdatedTime)}
        />
        <EditFormField
          error={errors.numberOfEmployees}
          label="Number of Employees"
          onChange={raw => {
            handleNumberChange('numberOfEmployees', raw);
          }}
          type="number"
          value={formatNumber(value.numberOfEmployees)}
        />
        <EditFormField
          error={errors.warmIntroProcessCount}
          label="Warm Intro Process Count"
          onChange={raw => {
            handleNumberChange('warmIntroProcessCount', raw);
          }}
          type="number"
          value={formatNumber(value.warmIntroProcessCount)}
        />
        <EditFormField
          label="Warm Intro Process Date"
          onChange={warmIntroProcessDate => {
            updateField('warmIntroProcessDate', warmIntroProcessDate);
          }}
          type="date"
          value={toDateInputValue(value.warmIntroProcessDate)}
        />
        <EditFormField
          label="Last Activity Added On"
          onChange={lastActivityAddedOn => {
            updateField('lastActivityAddedOn', lastActivityAddedOn);
          }}
          type="date"
          value={toDateInputValue(value.lastActivityAddedOn)}
        />
        <EditFormField
          label="Last Note Added On"
          onChange={lastNoteAddedOn => {
            updateField('lastNoteAddedOn', lastNoteAddedOn);
          }}
          type="date"
          value={toDateInputValue(value.lastNoteAddedOn)}
        />
        <EditFormField
          label="Last Synched to Notion"
          onChange={lastSynchedToNotion => {
            updateField('lastSynchedToNotion', lastSynchedToNotion);
          }}
          type="date"
          value={toDateInputValue(value.lastSynchedToNotion)}
        />
      </div>
    </EditDialog>
  );
}
